import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Dialog from '../common/Dialog';
import { config } from '../../config/config';
import { showToast } from '../utils/Toast';

const get = (path) => fetch(`${config.apiUrl}${path}`, {
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
}).then((r) => r.json());

const put = (path, body) => fetch(`${config.apiUrl}${path}`, {
  method: 'PUT',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
  body: JSON.stringify(body)
}).then((r) => r.json());

const Toggle = ({ checked, onChange, label, sub }) => (
  <label className="ns-toggle-row">
    <div className="ns-toggle-text">
      <span className="ns-toggle-label">{label}</span>
      {sub && <span className="ns-toggle-sub">{sub}</span>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`ns-toggle-track ${checked ? 'ns-toggle-on' : ''}`}
    >
      <span className="ns-toggle-thumb" />
    </button>
  </label>
);

const NotificationSettingsDialog = ({ isOpen, onClose }) => {
  const role = useSelector((s) => s.auth.user?.profile?.role);
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin' || isSuperAdmin;

  const [me, setMe] = useState({
    dailyDigest: true,
    crewAssigned: true,
    scheduleUpdates: true,
    projectLinked: true
  });
  const [global, setGlobal] = useState({
    emailNotificationsEnabled: true,
    dailyDigestEnabled: true,
    dailyDigestHour: 17,
    digestGroupEmail: ''
  });
  const [savingMe, setSavingMe] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [loadingMe, setLoadingMe] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingMe(true);
    get('/api/settings/notifications/me').then((d) => {
      if (d.success && d.notifications) {
        setMe((prev) => ({ ...prev, ...d.notifications }));
      }
    }).finally(() => setLoadingMe(false));

    if (isAdmin) {
      setLoadingGlobal(true);
      get('/api/settings/calendar').then((d) => {
        if (d.success && d.calendarSettings) {
          setGlobal((g) => ({ ...g, ...d.calendarSettings }));
        }
      }).finally(() => setLoadingGlobal(false));
    }
  }, [isOpen, isAdmin]);

  const saveMe = async () => {
    setSavingMe(true);
    try {
      const r = await put('/api/settings/notifications/me', me);
      if (r.success) showToast('Preferences saved', 'success');
      else showToast(r.message || 'Failed to save', 'error');
    } catch {
      showToast('Network error', 'error');
    } finally {
      setSavingMe(false);
    }
  };

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      const r = await put('/api/settings/calendar', global);
      if (r.success) showToast('Calendar settings saved', 'success');
      else showToast(r.message || 'Failed', 'error');
    } catch {
      showToast('Network error', 'error');
    } finally {
      setSavingGlobal(false);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => {
    const label = i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`;
    return { value: i, label };
  });

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="ns-dialog">
        {/* Header */}
        <div className="ns-header">
          <div className="ns-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16l-2-3z" />
              <path d="M10 19a2 2 0 004 0" />
            </svg>
          </div>
          <div className="ns-header-text">
            <h2 className="ns-title">Notification Preferences</h2>
            <p className="ns-subtitle">Choose what you want to be notified about</p>
          </div>
          <button className="ns-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="ns-body">
          {/* My Notifications */}
          <div className="ns-section">
            <div className="ns-section-header">
              <span className="ns-section-title">My Notifications</span>
              <span className="ns-section-badge">Email</span>
            </div>

            {loadingMe ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
            ) : (
              <>
                <div className="ns-card">
                  <Toggle
                    checked={me.dailyDigest}
                    onChange={(v) => setMe({ ...me, dailyDigest: v })}
                    label="Daily schedule digest"
                    sub="Receive tomorrow's full schedule each evening"
                  />
                  <Toggle
                    checked={me.crewAssigned}
                    onChange={(v) => setMe({ ...me, crewAssigned: v })}
                    label="Crew assignments"
                    sub="When a crew is assigned or updated on the schedule"
                  />
                  <Toggle
                    checked={me.scheduleUpdates}
                    onChange={(v) => setMe({ ...me, scheduleUpdates: v })}
                    label="Schedule changes"
                    sub="When events you're involved in are moved or modified"
                  />
                  <Toggle
                    checked={me.projectLinked}
                    onChange={(v) => setMe({ ...me, projectLinked: v })}
                    label="Project linked to schedule"
                    sub="When a project is linked to a calendar event"
                  />
                </div>

                <button className="ns-save-btn" onClick={saveMe} disabled={savingMe}>
                  {savingMe ? 'Saving…' : 'Save my preferences'}
                </button>
              </>
            )}
          </div>

          {/* Global Settings (Super Admin) */}
          {isSuperAdmin && (
            <div className="ns-section">
              <div className="ns-section-header">
                <span className="ns-section-title">Global Calendar Settings</span>
                <span className="ns-section-badge ns-section-badge-admin">Super Admin</span>
              </div>

              {loadingGlobal ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
              ) : (
                <>
                  <div className="ns-card">
                    <Toggle
                      checked={global.emailNotificationsEnabled}
                      onChange={(v) => setGlobal({ ...global, emailNotificationsEnabled: v })}
                      label="Email notifications"
                      sub="Master switch — enable or disable all event-related emails"
                    />
                    <Toggle
                      checked={global.dailyDigestEnabled}
                      onChange={(v) => setGlobal({ ...global, dailyDigestEnabled: v })}
                      label="Automatic daily digest"
                      sub="Send tomorrow's schedule to all opted-in users automatically"
                    />
                  </div>

                  <div className="ns-card-grid">
                    <div className="ns-field">
                      <label className="ns-field-label">Digest send time</label>
                      <select
                        className="ns-field-input"
                        value={global.dailyDigestHour}
                        onChange={(e) => setGlobal({ ...global, dailyDigestHour: Number(e.target.value) })}
                      >
                        {hours.map((h) => (
                          <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ns-field">
                      <label className="ns-field-label">Group email (optional)</label>
                      <input
                        className="ns-field-input"
                        placeholder="team@company.com"
                        value={global.digestGroupEmail || ''}
                        onChange={(e) => setGlobal({ ...global, digestGroupEmail: e.target.value })}
                      />
                    </div>
                  </div>

                  <button className="ns-save-btn" onClick={saveGlobal} disabled={savingGlobal}>
                    {savingGlobal ? 'Saving…' : 'Save global settings'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ns-footer">
          <button className="ns-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
